"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Box,
  Flex,
  Button,
  Dialog,
  Portal,
  CloseButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuMenu } from "react-icons/lu";
import { Logo } from "@/components/ui/logo";
import { SELF_SERVE_PRICING_ENABLED } from "@/lib/constants/billing";
import { useAuthSession } from "@/lib/auth/use-auth-session";

/* Hallmark · nav: N1b SaaS three-section · genre: modern-minimal · frost-on-scroll */
/* Replaced N1a edge-aligned: content destinations (Destinations/Blog/Pricing) and
   the account utility (Sign in) sat in one undifferentiated ghost-button row, so a
   visitor could not tell a product surface from an account link. Regrouped into three
   sections — brand left, content centred, account right — with the content links
   demoted from ghost buttons to plain text links to break the false uniformity. */
export function PublicHeader({
  ctaVariant = "primary",
}: {
  /**
   * `secondary` on any page that sells something else.
   *
   * `Get started` is the *tracking plan's* ask. On the destination funnel the
   * filled CTA belongs to the city report, and design.md § CTA voice is
   * explicit that a second commercial ask on the same page runs in secondary
   * voice. Because this header is `position: fixed`, leaving it filled put two
   * competing products in every single viewport of that funnel — the same
   * cross-sell that was just deleted from the page body, surviving in chrome.
   */
  ctaVariant?: "primary" | "secondary";
} = {}) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, isLoading } = useAuthSession();

  return (
    <>
      <Box
        as="header"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={100}
        bg="color-mix(in srgb, var(--color-background) 82%, transparent)"
        backdropFilter="saturate(180%) blur(12px)"
        borderBottom="1px solid var(--color-border)"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <Flex
          as="nav"
          aria-label="Primary"
          alignItems="center"
          justifyContent="space-between"
          h={{ base: "60px", md: "68px" }}
          px={{ base: 5, md: 10 }}
        >
          <Link href="/" aria-label="OTAlytics home">
            <Box cursor="pointer" display="flex" alignItems="center" _hover={{ opacity: 0.8 }}>
              <Box display={{ base: "block", md: "none" }}>
                <Logo withText={false} />
              </Box>
              <Box display={{ base: "none", md: "block" }}>
                <Logo withText />
              </Box>
            </Box>
          </Link>

          <Flex
            flex="1 1 0"
            justifyContent="center"
            alignItems="center"
            gap={{ base: 4, md: 6 }}
            display={{ base: "none", md: "flex" }}
          >
            <Link href="/destinations">
              <Text
                as="span"
                display="inline-flex"
                alignItems="center"
                minH="40px"
                fontSize="sm"
                fontWeight="500"
                color="var(--color-text-secondary)"
                whiteSpace="nowrap"
                transition="var(--transition-fast)"
                _hover={{ color: "var(--color-text-primary)" }}
              >
                Destinations
              </Text>
            </Link>
            <Link href="/blog">
              <Text
                as="span"
                display="inline-flex"
                alignItems="center"
                minH="40px"
                fontSize="sm"
                fontWeight="500"
                color="var(--color-text-secondary)"
                whiteSpace="nowrap"
                transition="var(--transition-fast)"
                _hover={{ color: "var(--color-text-primary)" }}
              >
                Blog
              </Text>
            </Link>
            {SELF_SERVE_PRICING_ENABLED && (
              <Link href="/pricing">
                <Text
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  minH="40px"
                  fontSize="sm"
                  fontWeight="500"
                  color="var(--color-text-secondary)"
                  whiteSpace="nowrap"
                  transition="var(--transition-fast)"
                  _hover={{ color: "var(--color-text-primary)" }}
                >
                  Pricing
                </Text>
              </Link>
            )}
          </Flex>

          <Flex alignItems="center" gap={{ base: 2, md: 3 }}>
            <Box
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              gap={3}
              minH="40px"
            >
              {!isLoading &&
                (user ? (
                  <Link href="/dashboard">
                    <Button
                      size="sm"
                      bg="var(--color-primary)"
                      color="var(--color-on-primary)"
                      fontWeight="600"
                      borderRadius="var(--radius-sm)"
                      px={4}
                      whiteSpace="nowrap"
                      _hover={{ bg: "var(--color-primary-hover)" }}
                      _active={{ bg: "var(--color-primary-strong)" }}
                    >
                      Dashboard
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link href="/login">
                      <Text
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        minH="40px"
                        fontSize="sm"
                        fontWeight="500"
                        color="var(--color-text-secondary)"
                        whiteSpace="nowrap"
                        transition="var(--transition-fast)"
                        _hover={{ color: "var(--color-text-primary)" }}
                      >
                        Sign in
                      </Text>
                    </Link>

                    <Link href="/signup">
                      <Button
                        size="sm"
                        bg={
                          ctaVariant === "primary" ? "var(--color-primary)" : "transparent"
                        }
                        color={
                          ctaVariant === "primary"
                            ? "var(--color-on-primary)"
                            : "var(--color-text-primary)"
                        }
                        border={
                          ctaVariant === "primary"
                            ? undefined
                            : "1px solid var(--color-border-strong)"
                        }
                        fontWeight="600"
                        borderRadius="var(--radius-sm)"
                        px={4}
                        whiteSpace="nowrap"
                        _hover={
                          ctaVariant === "primary"
                            ? { bg: "var(--color-primary-hover)" }
                            : { borderColor: "var(--color-primary)" }
                        }
                        _active={
                          ctaVariant === "primary"
                            ? { bg: "var(--color-primary-strong)" }
                            : undefined
                        }
                      >
                        Get started
                      </Button>
                    </Link>
                  </>
                ))}
            </Box>

            <Button
              size="sm"
              variant="ghost"
              minW="36px"
              h="36px"
              px={0}
              color="var(--color-text-secondary)"
              display={{ base: "flex", md: "none" }}
              onClick={() => setIsMobileOpen(true)}
              aria-label="Open navigation menu"
              _hover={{ color: "var(--color-text-primary)", bg: "var(--color-surface-hover)" }}
            >
              <LuMenu size={20} />
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Dialog.Root open={isMobileOpen} onOpenChange={(e) => setIsMobileOpen(e.open)}>
        <Portal>
          <Dialog.Backdrop bg="blackAlpha.500" backdropFilter="blur(4px)" />
          <Dialog.Positioner display="flex" alignItems="stretch" justifyContent="flex-start">
            <Dialog.Content
              w="280px"
              maxW="85vw"
              h="100dvh"
              bg="var(--color-surface)"
              borderRadius="0"
              borderRight="0.5px solid var(--color-border)"
              overflow="hidden"
            >
              <Dialog.Header px={5} py={5} borderBottom="0.5px solid var(--color-border)">
                <Flex alignItems="center" justifyContent="space-between">
                  <Logo withText />
                  <Dialog.CloseTrigger asChild>
                    <CloseButton
                      size="sm"
                      color="var(--color-text-secondary)"
                      _hover={{ bg: "var(--color-surface-hover)", color: "var(--color-text-primary)" }}
                    />
                  </Dialog.CloseTrigger>
                </Flex>
              </Dialog.Header>
              <Dialog.Body
                px={5}
                py={6}
                display="flex"
                flexDirection="column"
                gap={4}
                overflowY="auto"
              >
                <Stack gap={2}>
                  <Link href="/destinations" onClick={() => setIsMobileOpen(false)}>
                    <Box
                      px={4}
                      py={3}
                      minH="44px"
                      borderRadius="var(--radius-sm)"
                      color="var(--color-text-primary)"
                      _hover={{ bg: "var(--color-surface-hover)" }}
                    >
                      <Text fontWeight="500">Destinations</Text>
                    </Box>
                  </Link>
                  <Link href="/blog" onClick={() => setIsMobileOpen(false)}>
                    <Box
                      px={4}
                      py={3}
                      minH="44px"
                      borderRadius="var(--radius-sm)"
                      color="var(--color-text-primary)"
                      _hover={{ bg: "var(--color-surface-hover)" }}
                    >
                      <Text fontWeight="500">Blog</Text>
                    </Box>
                  </Link>
                  {SELF_SERVE_PRICING_ENABLED && (
                    <Link href="/pricing" onClick={() => setIsMobileOpen(false)}>
                      <Box
                        px={4}
                        py={3}
                        minH="44px"
                        borderRadius="var(--radius-sm)"
                        color="var(--color-text-primary)"
                        _hover={{ bg: "var(--color-surface-hover)" }}
                      >
                        <Text fontWeight="500">Pricing</Text>
                      </Box>
                    </Link>
                  )}
                  {!isLoading &&
                    (user ? (
                      <Link href="/dashboard" onClick={() => setIsMobileOpen(false)}>
                        <Box
                          px={4}
                          pt={3}
                          mt={3}
                          borderTop="1px solid var(--color-border-subtle)"
                          pb={3}
                          minH="44px"
                          borderRadius="var(--radius-sm)"
                          color="var(--color-text-primary)"
                          _hover={{ bg: "var(--color-surface-hover)" }}
                        >
                          <Text fontWeight="500">Dashboard</Text>
                        </Box>
                      </Link>
                    ) : (
                      <Link href="/login" onClick={() => setIsMobileOpen(false)}>
                        <Box
                          px={4}
                          pt={3}
                          mt={3}
                          borderTop="1px solid var(--color-border-subtle)"
                          pb={3}
                          minH="44px"
                          borderRadius="var(--radius-sm)"
                          color="var(--color-text-primary)"
                          _hover={{ bg: "var(--color-surface-hover)" }}
                        >
                          <Text fontWeight="500">Sign in</Text>
                        </Box>
                      </Link>
                    ))}
                </Stack>
                {!isLoading && !user && (
                  <Box mt="auto">
                    <Link href="/signup" onClick={() => setIsMobileOpen(false)}>
                      <Button
                        w="full"
                        bg="var(--color-primary)"
                        color="var(--color-on-primary)"
                        fontWeight="500"
                        borderRadius="var(--radius-sm)"
                        px={5}
                        _hover={{ bg: "var(--color-primary-hover)" }}
                        _active={{ bg: "var(--color-primary-strong)" }}
                      >
                        Get started
                      </Button>
                    </Link>
                  </Box>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
